import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/config";

const AuthCtx = React.createContext(null);
export const useAuth = () => React.useContext(AuthCtx);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=logged in
  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, let AuthCallback handle it first.
    // Otherwise checkAuth would race and 401 before the session cookie is set.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setUser(false);
      return;
    }
    axios.get(`${API}/auth/me`).then(r => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    return data;
  }, []);
  const signup = useCallback(async (email, password, name, phone, address = "", firstName = "", lastName = "") => {
    const { data } = await axios.post(`${API}/auth/customer/signup`, {
      email, password, name, phone, address,
      first_name: firstName,
      last_name: lastName,
    });
    setUser(data);
    return data;
  }, []);
  const refresh = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/auth/me`); setUser(data); return data; }
    catch { setUser(false); return null; }
  }, []);
  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { console.warn("logout call failed:", e?.message); }
    setUser(false);
  }, []);
  // Memoise the context value so consumers don't re-render on every AuthProvider parent render.
  const value = useMemo(() => ({ user, login, signup, refresh, logout }), [user, login, signup, refresh, logout]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const fmtErr = (d) => {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (d.msg) return d.msg;
  if (d.message) return d.message;
  return String(d);
};
